import React, { createContext, useContext, useState, useEffect } from 'react';

// Define available languages
export type Language = 'en' | 'es' | 'fr';

// Define translations for each language
export const translations = {
  en: {
    welcome: "Welcome to bluetooth$",
    securePayments: "Secure Payments",
    secureDesc: "All transactions are encrypted end-to-end",
    bluetoothTech: "Bluetooth Technology",
    bluetoothDesc: "Quick, secure connections to nearby devices",
    deviceStorage: "On-Device Storage",
    deviceDesc: "Your payment details never leave your device",
    privacyFirst: "Privacy First",
    privacyDesc: "We never sell or share your data",
    acceptTerms: "I accept the terms and conditions",
    readOur: "Please read our",
    privacyPolicy: "Privacy Policy",
    and: "and",
    termsService: "Terms of Service",
    getStarted: "Get Started",
    haveAccount: "Already have an account?",
    signIn: "Sign in",
    signUp: "Create Account",
    rights: "All rights reserved",
    languageSelect: "Select language",
    aiHelp: "AI Help",
    aiHelpIntro: "How can I assist you with the bluetooth$ app?",
    askQuestion: "Ask a question...",
    send: "Send",
    sendMoneyTitle: "Send Money Anywhere",
    sendMoneyDesc: "Transfer funds instantly using Bluetooth, email, or to any verified user account.",
    sendMoneyCta: "Send Money Now",
    createAccount: "Create Account"
  },
  es: {
    welcome: "Bienvenido a bluetooth$",
    securePayments: "Pagos Seguros",
    secureDesc: "Todas las transacciones están cifradas de extremo a extremo",
    bluetoothTech: "Tecnología Bluetooth",
    bluetoothDesc: "Conexiones rápidas y seguras con dispositivos cercanos",
    deviceStorage: "Almacenamiento en Dispositivo",
    deviceDesc: "Sus datos de pago nunca salen de su dispositivo",
    privacyFirst: "Privacidad Primero",
    privacyDesc: "Nunca vendemos ni compartimos sus datos",
    acceptTerms: "Acepto los términos y condiciones",
    readOur: "Por favor lea nuestra",
    privacyPolicy: "Política de Privacidad",
    and: "y",
    termsService: "Términos de Servicio",
    getStarted: "Comenzar",
    haveAccount: "¿Ya tienes una cuenta?",
    signIn: "Iniciar sesión",
    signUp: "Crear Cuenta",
    rights: "Todos los derechos reservados",
    languageSelect: "Seleccionar idioma",
    aiHelp: "Ayuda de IA",
    aiHelpIntro: "¿Cómo puedo ayudarte con la aplicación bluetooth$?",
    askQuestion: "Hacer una pregunta...",
    send: "Enviar",
    sendMoneyTitle: "Envía Dinero a Cualquier Lugar",
    sendMoneyDesc: "Transfiere fondos al instante mediante Bluetooth, correo electrónico o a cualquier cuenta de usuario verificada.",
    sendMoneyCta: "Enviar Dinero Ahora",
    createAccount: "Crear Cuenta"
  },
  fr: {
    welcome: "Bienvenue sur bluetooth$",
    securePayments: "Paiements Sécurisés",
    secureDesc: "Toutes les transactions sont chiffrées de bout en bout",
    bluetoothTech: "Technologie Bluetooth",
    bluetoothDesc: "Connexions rapides et sécurisées aux appareils à proximité",
    deviceStorage: "Stockage sur l'Appareil",
    deviceDesc: "Vos données de paiement ne quittent jamais votre appareil",
    privacyFirst: "Confidentialité d'Abord",
    privacyDesc: "Nous ne vendons ni ne partageons jamais vos données",
    acceptTerms: "J'accepte les termes et conditions",
    readOur: "Veuillez lire notre",
    privacyPolicy: "Politique de Confidentialité",
    and: "et",
    termsService: "Conditions d'Utilisation",
    getStarted: "Commencer",
    haveAccount: "Vous avez déjà un compte?",
    signIn: "Se connecter",
    signUp: "Créer un Compte",
    rights: "Tous droits réservés",
    languageSelect: "Sélectionner la langue",
    aiHelp: "Aide IA",
    aiHelpIntro: "Comment puis-je vous aider avec l'application bluetooth$?",
    askQuestion: "Poser une question...",
    send: "Envoyer",
    sendMoneyTitle: "Envoyez de l'Argent Partout",
    sendMoneyDesc: "Transférez des fonds instantanément via Bluetooth, e-mail ou vers n'importe quel compte utilisateur vérifié.",
    sendMoneyCta: "Envoyer de l'Argent Maintenant",
    createAccount: "Créer un Compte"
  }
};

type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: keyof typeof translations['en']) => string;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  // Check if there's a stored language preference
  useEffect(() => {
    const storedLanguage = localStorage.getItem('preferredLanguage') as Language;
    if (storedLanguage && ['en', 'es', 'fr'].includes(storedLanguage)) {
      setLanguage(storedLanguage);
    }
  }, []);

  // Save language preference to local storage when it changes
  useEffect(() => {
    localStorage.setItem('preferredLanguage', language);
  }, [language]);

  // Function to get translated text
  const t = (key: keyof typeof translations['en']) => {
    return translations[language][key] || translations['en'][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
